/**
 * Hybrid logical clock (engine.md §1, sync.md §2).
 *
 *   Hlc = `${ms padded to 13}-${counter base36 padded to 4}-${node}` — orders with string comparison.
 *
 * Stamp: ms = max(clock.now(), last.ms); counter = last.counter + 1 when ms == last.ms, else 0.
 * Receive: a remote Hlc larger than `last` becomes `last` (so the next stamp is larger still).
 * When the counter would overflow 4 base-36 digits (1,679,615 stamps in one millisecond) the
 * physical part is advanced by 1 ms instead.
 */

import type { EpochMs, Hlc } from '../contracts/ids.js';
import type { Clock } from '../contracts/platform.js';

/** Sorts below every real clock value: seed rows lose to any edit. */
export const SEED_HLC = '0000000000000-0000-seed' as Hlc;

const MAX_COUNTER = 36 ** 4 - 1;
const HLC_PATTERN = /^(\d{13})-([0-9a-z]{4})-([a-z0-9_]+)$/;

export interface HlcParts {
  ms: number;
  counter: number;
  node: string;
}

export function formatHlc(parts: HlcParts): Hlc {
  return `${String(parts.ms).padStart(13, '0')}-${parts.counter.toString(36).padStart(4, '0')}-${parts.node}` as Hlc;
}

export function parseHlc(value: string): HlcParts | null {
  const m = HLC_PATTERN.exec(value);
  if (!m) return null;
  return { ms: Number(m[1]), counter: parseInt(m[2] as string, 36), node: m[3] as string };
}

export function isHlc(value: unknown): value is Hlc {
  return typeof value === 'string' && HLC_PATTERN.test(value);
}

export function compareHlc(a: string, b: string): -1 | 0 | 1 {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Physical milliseconds of an Hlc (0 for the seed value or garbage). */
export function hlcMs(value: string): number {
  return parseHlc(value)?.ms ?? 0;
}

/** The node part of an Hlc ('' when unparseable). */
export function hlcNode(value: string): string {
  return parseHlc(value)?.node ?? '';
}

export class HlcClock {
  private lastMs: number;
  private lastCounter: number;

  constructor(
    readonly node: string,
    private readonly clock: Clock,
    last?: string | null,
  ) {
    const parsed = last ? parseHlc(last) : null;
    this.lastMs = parsed?.ms ?? 0;
    this.lastCounter = parsed?.counter ?? 0;
  }

  /** The largest Hlc this clock has stamped or received (as a string with this node). */
  get last(): Hlc {
    return formatHlc({ ms: this.lastMs, counter: this.lastCounter, node: this.node });
  }

  /** A new Hlc, strictly greater than every one stamped or received before. */
  stamp(): Hlc {
    const now = Math.floor(this.clock.now() as EpochMs);
    if (now > this.lastMs) {
      this.lastMs = now;
      this.lastCounter = 0;
    } else if (this.lastCounter >= MAX_COUNTER) {
      this.lastMs += 1;
      this.lastCounter = 0;
    } else {
      this.lastCounter += 1;
    }
    return this.last;
  }

  /** Receive rule: advance past a remote Hlc. Unparseable or older values are ignored. */
  receive(remote: string): void {
    const p = parseHlc(remote);
    if (!p) return;
    if (p.ms > this.lastMs || (p.ms === this.lastMs && p.counter > this.lastCounter)) {
      this.lastMs = p.ms;
      this.lastCounter = p.counter;
    }
  }
}
